import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { addItineraryToStore, readActiveTripName } from "@majourneys/discover";
import { useThreadAnchor } from "../hooks/use-thread-anchor";
import { useAiChat, type ChatLang } from "../hooks/use-ai-chat";
import { useChatHistory } from "../hooks/use-chat-history";
import { conciergeActions, useConciergeStore } from "../state/concierge-store";
import type { AiMatch, Itinerary } from "../api/ai.api";
import { AssistantTurn, ChatError, PendingTurn, UserBubble } from "./assistant-turn";
import { ChatComposer } from "./chat-composer";
import { ChatHistoryMenu } from "./chat-history-menu";
import { ChatToolbar } from "./chat-toolbar";
import { ChatWelcome } from "./chat-welcome";
import { ConciergeSignInGate } from "./concierge-sign-in-gate";
import { RADIUS, SOFT } from "./soft-tokens";
import { panelStyle } from "../../../shared/ui/card-frame";
import { solid } from "../../../shared/ui/pewter-edge";

const WINDOW_BAND = panelStyle(RADIUS.card, solid(SOFT.ground));
const WINDOW_FRAME = {
  ...WINDOW_BAND,
  boxShadow: `${WINDOW_BAND.boxShadow}, 0 30px 80px -20px rgba(17,14,57,0.35)`,
};

interface GlassConciergeProps {
  open: boolean;
  onClose: () => void;
}

/**
 * MaJourneys AI concierge, the modal. Same look as the phone's chat surface
 * (apps/mobile/src/features/discover/components/chat/chat-surface.tsx): the
 * soft-neutral ground, navy user bubbles, the orb as the assistant's only
 * identity, the phone's minimal cards in a grid, one gold send button.
 *
 * Every part below the header is shared with the Discover panel
 * (`DiscoverChat`) through `assistant-turn.tsx`, `chat-composer.tsx` and
 * `chat-welcome.tsx`, per the ai-chat-parity rule.
 *
 * Add-to-trip state is held locally in the modal (wired into the persistent
 * itinerary feature in a follow-up). External surfaces (hero search) push
 * prompts in via `conciergeActions.openWith()`, which the modal auto-sends.
 */
export function GlassConcierge({ open, onClose }: GlassConciergeProps): ReactNode {
  const { i18n, t } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) ?? "en") as ChatLang;
  const isRtl = lang === "ar";
  const history = useChatHistory();
  const {
    messages,
    loading,
    error,
    send,
    stop,
    retry,
    retryFailed,
    isLive,
    setMessages,
    gated,
    gatedQuery,
    clearGate,
  } = useAiChat({
    lang,
    limit: 20,
  });
  const [input, setInput] = useState("");
  /** The earlier message being edited (C2); the next send replaces it. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [inTrip, setInTrip] = useState<Set<string>>(new Set());
  const [lastQuery, setLastQuery] = useState("");
  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { pendingQuery, pendingNonce } = useConciergeStore();
  const lastSentNonce = useRef(0);

  const activeTripName = readActiveTripName();

  const addItineraryToTrip = (itinerary: Itinerary, itinMatches: AiMatch[], mode: "new" | "active") => {
    const tripId = addItineraryToStore(itinerary, itinMatches, mode, t("trip.ai_default_name"));
    if (!tripId) return;
    onClose();
    if (typeof window !== "undefined") {
      window.location.assign(`/${lang}/discover?utility=trips`);
    }
  };

  // Hydrate from the active conversation in history once it loads.
  // `useChatHistory` reads localStorage in an effect, so its `loaded`
  // flag flips to true after the first render. We seed the local chat
  // ONCE off the loaded snapshot and then mark hydration done — any
  // later changes to `activeMessages` come from our own persist and
  // should be ignored to avoid an infinite re-hydrate loop.
  const hydratedRef = useRef(false);
  const { persistMessages, activeMessages, loaded } = history;
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!loaded) return;
    if (activeMessages.length > 0) setMessages(activeMessages);
    hydratedRef.current = true;
  }, [loaded, activeMessages, setMessages]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (messages.length === 0) return;
    persistMessages(messages);
  }, [messages, persistMessages]);

  const toggleTrip = (id: string) =>
    setInTrip((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submit = (query: string) => {
    const q = query.trim();
    if (!q || loading) return;
    setLastQuery(q);
    setInput("");
    const replaceFrom = editingId;
    setEditingId(null);
    void send(q, replaceFrom ? { replaceFrom } : undefined);
  };

  /** Hands a message back to the composer; the next send cuts the thread there. */
  const startEdit = (id: string, text: string) => {
    setEditingId(id);
    setInput(text);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setInput("");
  };
  /** Puts words in the composer without sending them ("Change" on a proposal). */
  const compose = (text: string) => {
    setEditingId(null);
    setInput(text);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Auto-send queries dispatched from outside (hero search, etc.).
  useEffect(() => {
    if (!open) return;
    if (!pendingQuery) return;
    if (pendingNonce === lastSentNonce.current) return;
    lastSentNonce.current = pendingNonce;
    setLastQuery(pendingQuery);
    void send(pendingQuery);
    conciergeActions.clearPending();
  }, [open, pendingQuery, pendingNonce, send]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useThreadAnchor(scrollerRef, messages, loading);

  // Focus input on open
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  if (!open) return null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  /**
   * Back to the same conversation, not a fresh one. The thread is untouched,
   * so all this does is put the composer back and hand over the question the
   * gate refused, ready to send.
   */
  const resumeAfterSignIn = () => {
    const pending = gatedQuery;
    clearGate();
    if (pending) setInput(pending);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-3 pt-20 sm:p-8 sm:pt-24"
      role="dialog"
      aria-modal="true"
      aria-label={t("ai_concierge.title", { defaultValue: "MaJourneys AI" })}
    >
      <button
        type="button"
        aria-label={t("ai_concierge.close", { defaultValue: "Close MaJourneys AI" })}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ background: "rgba(4,0,51,0.45)", backdropFilter: "blur(6px)" }}
      />

      <div
        className="relative z-10 flex h-[calc(88dvh_/_var(--mj-ui-zoom,1))] max-h-[780px] w-full min-w-0 max-w-[820px] flex-col overflow-hidden"
        style={{
          // The pewter band around the chat's own ground (owner, 2026-10-11),
          // the same 4px band as the desktop AI pill's window.
          ...WINDOW_FRAME,
          animation: "mjFadeIn 0.4s cubic-bezier(.2,.8,.2,1)",
        }}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <ChatHeader
          tripCount={inTrip.size}
          onClose={onClose}
          historyMenu={
            <ChatHistoryMenu
              conversations={history.conversations}
              activeId={history.activeId}
              onNewChat={() => {
                history.newChat();
                setEditingId(null);
                setMessages([]);
                hydratedRef.current = true;
                window.setTimeout(() => inputRef.current?.focus(), 0);
              }}
              onSelect={(id) => {
                const msgs = history.selectChat(id);
                setEditingId(null);
                setMessages(msgs);
              }}
              onDelete={(id) => history.deleteChat(id)}
            />
          }
        />

        <div
          ref={scrollerRef}
          className="relative min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 [scrollbar-width:thin] [scrollbar-color:rgba(17,14,57,0.18)_transparent]"
        >
          <div className="mx-auto flex max-w-[640px] flex-col gap-4">
            {messages.length === 0 && !error && <ChatWelcome onPick={submit} />}

            {messages.map((m, i) =>
              m.kind === "user" ? (
                <UserBubble
                  key={m.id}
                  id={m.id}
                  text={m.text}
                  onEdit={loading ? undefined : () => startEdit(m.id, m.text)}
                />
              ) : m.kind === "pending" ? (
                <PendingTurn key={m.id} steps={m.steps} />
              ) : (
                <AssistantTurn
                  key={m.id}
                  message={m}
                  lang={lang}
                  inTrip={inTrip}
                  onAdd={toggleTrip}
                  onAddItinerary={addItineraryToTrip}
                  activeTripName={activeTripName}
                  onRefine={submit}
                  onCompose={compose}
                  shareSessionId={history.activeSessionId}
                  conversationId={history.activeId}
                  isLatest={i === messages.length - 1 && isLive(m.id)}
                />
              ),
            )}

            {error && (
              <ChatError
                offerTeam={retryFailed}
                onRetry={
                  messages.some((m) => m.kind === "user")
                    ? retry
                    : lastQuery
                      ? () => submit(lastQuery)
                      : undefined
                }
              />
            )}
          </div>
        </div>

        {/* The gate stands exactly where the composer stood, so the thread
            above it is never covered — and the composer is REMOVED rather
            than disabled, because a dead field invites typing into nothing. */}
        {gated ? (
          <div className="px-4 pb-4 pt-3 sm:px-6">
            <ConciergeSignInGate onSignedIn={resumeAfterSignIn} />
          </div>
        ) : (
          <ChatComposer
            value={input}
            onChange={setInput}
            onSubmit={onSubmit}
            loading={loading}
            inputRef={inputRef}
            onStop={stop}
            editing={!!editingId}
            onCancelEdit={cancelEdit}
          />
        )}
      </div>

      <style>{`
        @keyframes mjFadeIn {
          0% { transform: scale(0.96) translateY(8px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ---------- Header ---------- */
/**
 * The phone's session bar: a round history control, the title, and the
 * actions. No sparkle badge and no "online" line: the orb below is the
 * identity, and a status dot on a service that is always up says nothing.
 */
function ChatHeader({
  tripCount,
  onClose,
  historyMenu,
}: {
  tripCount: number;
  onClose: () => void;
  historyMenu?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="relative flex items-center justify-between gap-3 px-4 pb-2 pt-4 sm:px-6">
      <div className="text-[15px] font-bold" style={{ color: SOFT.ink }}>
        {t("ai_concierge.title", { defaultValue: "MaJourneys AI" })}
      </div>
      <div className="flex items-center gap-1.5 text-[11.5px] font-medium">
        {/* Same capsule as the Discover panel, two segments instead of three. */}
        <ChatToolbar>{historyMenu}</ChatToolbar>
        {tripCount > 0 ? (
          <span
            className="hidden items-center gap-1 rounded-full px-2.5 py-1 sm:inline-flex"
            style={{ background: SOFT.controlFill, border: `1px solid ${SOFT.controlEdge}`, color: SOFT.ink }}
          >
            <Icon name="ri-list-check" size={12} aria-hidden="true" />
            {t("ai_concierge.trip_count", { defaultValue: "{{count}} in trip", count: tripCount })}
          </span>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-[rgba(4,0,51,0.1)]"
          style={{ color: SOFT.ink, background: SOFT.fill }}
          aria-label={t("ai_concierge.close", { defaultValue: "Close" })}
        >
          <Icon name="ri-close-line" size={17} aria-hidden="true" />
        </button>
      </div>
    </header>
  );
}
